import React, { useState } from 'react'
import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  GDriveMatchingTableProps,
  googleUserAtom,
  getDocx,
} from 'infrastructure/googleDrive'
import { generatedMetaStructureAtom } from '../atoms'
import { parseHtmlString } from 'shared/utils'
import { LessonStructure } from '../types'
import {
  convertToKiddomHTMLStyle,
  getH3sBeforeFirstH2,
  getSectionContent,
  sliceContentsByCondition,
} from '../utilities/utility'
import { Update } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { gradeAtom } from './GradeSelector/grade.atom'
import { requiredLessonHeaders } from './lessonConfig'

function getGrade(table: GDriveMatchingTableProps): number {
  if (!table.rows || !table.rows.length) return -1
  if (table.rows[0].grade === 'K') return 0
  return Number(table.rows[0].grade) ? Number(table.rows[0].grade) : -1
}

export function ParseAll(props: GDriveMatchingTableProps) {
  const [parsing, setParsing] = useState(false)
  const [metaStructure, setMetaStructure] = useAtom(generatedMetaStructureAtom)
  const [_, setGrade] = useAtom(gradeAtom)
  const { credential } = useAtomValue(googleUserAtom)

  const { rows } = props
  const files = rows.map((row) => row.file)

  if (!credential) return null
  if (!files || !files.length) return null

  const parseDocs = () => {
    const promises = rows.map(async (row) => {
      const content = await getDocx(credential.accessToken, row.file.id)
      const asHtml = parseHtmlString(content)
      return {
        ...row,
        body: asHtml.body,
      }
    })
    setGrade(getGrade(props))
    const newMetaStructure = {
      grade: getGrade(props),
      lessons: [] as LessonStructure[],
    }

    Promise.all(promises).then((result) => {
      result.forEach((row) => {
        const lessonStructure: LessonStructure = {
          name: '',
          unit: row.unit,
          grade: newMetaStructure.grade,
          id: row.lesson,
          parts: [],
        }
        const cleanedBody = convertToKiddomHTMLStyle({
          content: row.body.innerHTML,
        })
        if (!cleanedBody) return
        lessonStructure.name =
          cleanedBody?.querySelector('h1')?.textContent ?? ''
        const slicesh2 = sliceContentsByCondition({
          contents: (cleanedBody?.children as unknown as Element[]) || [],
          condition: 'tagName',
          type: 'H2',
        })
        lessonStructure.parts = []

        const lessonIntroHeaders = getH3sBeforeFirstH2(cleanedBody)
        const lessonIntroSections: Record<string, string> = {}

        requiredLessonHeaders.forEach((expectedTitle) => {
          const matchingH3 = lessonIntroHeaders.find(
            (h3) => h3.textContent?.trim().toUpperCase() === expectedTitle,
          )
          if (!matchingH3) return

          lessonIntroSections[expectedTitle] = getSectionContent(matchingH3)
        })

        Object.entries(lessonIntroSections).forEach(([key, value]) => {
          lessonStructure.parts.push({
            name: key.toUpperCase(),
            content: value,
          })
        })

        Object.entries(slicesh2).forEach(([key, value]) => {
          const slicesh3 = sliceContentsByCondition({
            contents: parseHtmlString(value).body
              .children as unknown as Element[],
            condition: 'tagName',
            type: 'H3',
          })
          lessonStructure.body = value
          lessonStructure.parts.push({
            name: key,
            content: value,
            parts: Object.entries(slicesh3).map(([key, value]) => ({
              name: key,
              body: parseHtmlString(value).body,
              content: value,
            })),
          })
        })
        newMetaStructure.lessons = [
          ...newMetaStructure.lessons,
          lessonStructure,
        ]
      })
      setMetaStructure(newMetaStructure)
      setParsing(false)
    })
  }

  const parseAll = () => {
    setParsing(true)
    return parseDocs()
  }

  return (
    <LoadingButton
      variant="contained"
      onClick={() => parseAll()}
      loading={parsing}
      color="primary"
      startIcon={<Update />}
    >
      Parse All
    </LoadingButton>
  )
}
